import type {
  BaseServiceOptions,
  Pagination,
  TBaseCatalogs,
  TBaseFilePO,
  TRecordAction,
  TRequestCatalog,
} from '@/core/types'
import {
  type TProjectManagement,
  moduleServiceHandlers,
  catalogServiceHandlers,
  type TProjectsManagement,
  serviceConfig,
} from '../../..'
import { defaultPagination, useNotification, useServiceHandlers } from '@/core'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { AxiosError } from 'axios'
import { toValue } from 'vue'
import { TCustomer } from '@/modules/customers'
import { useCatalogStore } from '@/core/store/catalogs'

const { model } = serviceConfig
const { customers } = serviceConfig.catalogs
const { customersServiceHandlers, categoriesCatalog } = catalogServiceHandlers

export const useModuleQuery = (options: BaseServiceOptions<Pagination>) => {
  const { currentPage, filters, handlers, enableQuery, modelId } = options
  const store = useCatalogStore()
  const { errorHttpNotification } = useNotification()
  const serviceHandlers = useServiceHandlers<TProjectManagement>({
    model,
    currentPage: toValue(currentPage) ?? 1,
    filterParams: filters?.loadRecords ?? '',
  })

  const getTotalPercentage = (total: number, quantity: number) => {
    if (!total) return 0

    const result = (quantity / total) * 100

    return result >= 100 ? 100 : Number(result.toFixed(2))
  }

  const deleteRecord = useMutation<void, AxiosError, TProjectManagement>(
    (payload) => moduleServiceHandlers.deleteRecord(payload),
    {
      onSuccess: (_, projectManagement) =>
        serviceHandlers.deleteRecord(projectManagement),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const deleteRecords = useMutation<void, AxiosError, Array<number>>(
    (payload) => moduleServiceHandlers.deleteRecords({ ids: payload }),
    {
      onSuccess: (_, ids) => serviceHandlers.deleteRecords(ids),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const editRecord = useMutation<
    TProjectManagement,
    AxiosError,
    TProjectManagement
  >(
    (payload: TProjectManagement) =>
      moduleServiceHandlers.editRecord(payload, payload.id),
    {
      onSuccess: () => serviceHandlers.invalidateQueries(),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const addRecord = useMutation<
    TProjectManagement,
    AxiosError,
    TProjectManagement
  >((payload: TProjectManagement) => moduleServiceHandlers.addRecord(payload), {
    onSuccess: (payload) => serviceHandlers.addRecord(payload),
    onError: (error) => errorHttpNotification(error),
  })

  const actionRecord = useMutation<
    TProjectManagement,
    AxiosError,
    TRecordAction
  >((payload: TRecordAction) => moduleServiceHandlers.actionRecord(payload), {
    onSuccess: (payload) => serviceHandlers.updateRecord(payload),
    onError: (error) => errorHttpNotification(error),
  })

  const loadRecords = useQuery<TProjectsManagement, AxiosError>({
    queryKey: [model, currentPage, filters?.loadRecords],
    queryFn: () =>
      moduleServiceHandlers.loadRecords({
        dynamicFilters: filters?.loadRecords,
        page: currentPage,
      }),
    refetchOnWindowFocus: false,
    enabled: enableQuery?.loadRecords,
    onError: (error) => errorHttpNotification(error),
    onSuccess: (data) => {
      if (data.meta) {
        handlers?.onDataChange?.(data.meta ?? defaultPagination)
      }
    },
    select: (data) => ({
      ...data,
      data: data.data.map((record) => ({
        ...record,
        total_amount_percentage: getTotalPercentage(
          record.amount ?? 0,
          record.amount_invoiced ?? 0,
        ),
        pending_billing_percentage: getTotalPercentage(
          record.amount ?? 0,
          record.pending_billing ?? 0,
        ),
        total_payment_percentage: getTotalPercentage(
          record.amount ?? 0,
          record.amount_paid ?? 0,
        ),
      })),
    }),
  })

  const loadRecord = useQuery<TProjectManagement, AxiosError>({
    queryKey: [model, modelId],
    queryFn: ({ queryKey }) =>
      moduleServiceHandlers.loadRecord({
        id: Number(queryKey[1]),
      }),
    refetchOnWindowFocus: false,
    enabled: enableQuery?.loadRecord,
    onError: (error) => errorHttpNotification(error),
    onSuccess: (data) => {
      if (data.id) {
        handlers?.onLoadRecord?.(data)
      }
    },
    select: (data) => ({
      ...data,
      total_amount_percentage: getTotalPercentage(
        data.amount ?? 0,
        data.amount_invoiced ?? 0,
      ),
      pending_billing_percentage: getTotalPercentage(
        data.amount ?? 0,
        data.pending_billing ?? 0,
      ),
      total_payment_percentage: getTotalPercentage(
        data.amount ?? 0,
        data.amount_paid ?? 0,
      ),
    }),
  })

  const customersCatalog = useQuery<TCustomer[], AxiosError>({
    queryKey: [customers, filters?.customersCatalog],
    queryFn: () =>
      customersServiceHandlers.loadCatalog({
        dynamicFilters: filters?.customersCatalog,
      }),
    onError: (error) => errorHttpNotification(error),
    refetchOnWindowFocus: false,
    enabled: enableQuery?.customersCatalog,
  })

  const requestCatalog = useMutation<
    TBaseCatalogs,
    AxiosError,
    TRequestCatalog
  >((payload: Record<any, any>) => categoriesCatalog.requestCatalog(payload), {
    onSuccess: (payload) => {
      store.setCatalogs(payload)
    },
    onError: (error) => errorHttpNotification(error),
  })

  const actionUploadPo = useMutation<
    TProjectManagement,
    AxiosError,
    TBaseFilePO
  >(
    (payload: TProjectManagement & TBaseFilePO) =>
      moduleServiceHandlers.actionUploadPo(
        modelToFormData(payload, 'PUT'),
        payload.id,
      ),
    {
      onSuccess: (payload) => serviceHandlers.updateRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const modelToFormData = (
    payload: TProjectManagement & TBaseFilePO,
    method?: string,
  ) => {
    const formData = new FormData()
    if (payload?.file) {
      formData.append('file', payload.file)
    }

    if (method) {
      formData.append('_method', method)
    }

    return formData
  }

  return {
    loadRecords,
    loadRecord,
    addRecord,
    editRecord,
    deleteRecords,
    deleteRecord,
    actionRecord,
    customersCatalog,
    requestCatalog,
    actionUploadPo,
    serviceHandlers,
  }
}
